import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight, Quote, Star } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import { useConsultation } from "@/components/ConsultationModal";
import { SERVICES, STATS, CASE_STUDIES, TESTIMONIALS, MANIFESTO } from "@/data/content";

export function ServicesBento() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-services-bento">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          eyebrow="What we do"
          title="Eight disciplines. One accountable team."
          description="From first wireframe to 24/7 managed operations, every capability lives under one roof — so nothing gets lost between vendors."
          testid="services-section"
        />
        <Reveal delay={0.1}>
          <Link to="/services" className="group flex items-center gap-2 text-sm font-semibold text-cyan-300" data-testid="services-view-all-link">
            View all services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 4) * 0.08}>
            <Link
              to={s.href}
              data-testid={`service-card-${s.slug}`}
              className="card-glow group flex h-full flex-col rounded-2xl border border-white/10 bg-navy-900/60 p-6"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5 transition-colors group-hover:bg-cyan-400/15">
                <s.icon className="h-5 w-5 text-cyan-300" />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">{s.name}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s.tagline}</p>
              <span className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 transition-colors group-hover:text-cyan-300">
                Learn more <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Counter({ value, suffix, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay((value * eased).toFixed(decimals));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, decimals]);

  return (
    <span ref={ref} className="font-display text-4xl font-extrabold text-white sm:text-5xl">
      {display}
      <span className="text-gradient">{suffix}</span>
    </span>
  );
}

export function MetricsBand() {
  return (
    <section className="border-y border-white/5 bg-navy-900/40" data-testid="home-metrics-band">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-4 py-16 sm:px-6 lg:grid-cols-4 lg:px-8">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="text-center">
            <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function CaseStudies() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-case-studies">
      <SectionHeading
        eyebrow="Selected work"
        title="Results our clients can measure"
        description="A few recent projects for small local businesses across the UK."
        testid="case-studies-section"
      />
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {CASE_STUDIES.map((c, i) => (
          <Reveal key={c.client} delay={i * 0.1}>
            <article className="card-glow group overflow-hidden rounded-2xl border border-white/10 bg-navy-900/60" data-testid={`case-study-${c.client.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="spotlight relative overflow-hidden">
                <img src={c.image} alt={`${c.client} — ${c.title}`} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-transparent to-transparent" />
                <span className="absolute left-4 top-4 rounded-full border border-cyan-400/30 bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300 backdrop-blur">
                  {c.sector}
                </span>
              </div>
              <div className="p-6">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{c.client}</p>
                <h3 className="mt-2 font-display text-lg font-semibold text-white">{c.title}</h3>
                <p className="mt-3 flex items-center gap-2 text-sm font-medium text-cyan-300">
                  <Star className="h-4 w-4 fill-cyan-400 text-cyan-400" /> {c.result}
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Manifesto() {
  return (
    <section className="border-y border-white/5 bg-navy-900/40" data-testid="home-manifesto">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="The TechAxis manifesto"
          title="Four principles behind every build"
          testid="manifesto-section"
        />
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:grid-cols-2 lg:grid-cols-4">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.08} className="bg-navy-950">
              <div className="flex h-full flex-col p-7" data-testid={`manifesto-chapter-${m.n}`}>
                <span className="font-mono text-sm text-cyan-400/70">{m.n}</span>
                <h3 className="mt-4 font-display text-lg font-semibold text-white">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const t = TESTIMONIALS[index];

  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-testimonials">
      <SectionHeading eyebrow="Client voices" title="Trusted by teams who measure twice" align="center" testid="testimonials-section" />
      <div className="relative mx-auto mt-12 max-w-3xl">
        <Quote className="absolute -top-6 left-0 h-10 w-10 text-cyan-400/20" />
        <AnimatePresence mode="wait">
          <motion.figure
            key={index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="glass rounded-2xl p-8 sm:p-10"
            data-testid="testimonial-card"
          >
            <blockquote className="text-base leading-relaxed text-slate-200 sm:text-lg">“{t.quote}”</blockquote>
            <figcaption className="mt-6">
              <p className="font-display font-semibold text-white">{t.name}</p>
              <p className="text-sm text-slate-400">{t.role}</p>
            </figcaption>
          </motion.figure>
        </AnimatePresence>
        <div className="mt-6 flex justify-center gap-2.5">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show testimonial ${i + 1}`}
              onClick={() => setIndex(i)}
              data-testid={`testimonial-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === index ? "w-7 bg-cyan-400" : "w-2 bg-white/20 hover:bg-white/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTABand() {
  const { open } = useConsultation();
  return (
    <section className="relative overflow-hidden border-t border-white/5" data-testid="home-cta-band">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,102,255,0.18),transparent_65%)]" />
      <div className="relative mx-auto max-w-4xl px-4 py-24 text-center sm:px-6">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Ready when you are</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Let's turn your idea into <span className="text-gradient">working software</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm text-slate-400 sm:text-base">
            Start with a free 30-minute consultation or an instant estimate — no obligation, no jargon, no pressure.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/quote"
              data-testid="cta-get-quote-button"
              className="group flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_35px_rgba(0,102,255,0.4)] transition-all hover:bg-cyan-400 hover:text-ink"
            >
              Get a Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              type="button"
              onClick={open}
              data-testid="cta-consultation-button"
              className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
            >
              Book a Free Consultation
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
